import { Link } from "react-router-dom";
import { CaretRight, Warning } from "@phosphor-icons/react";
import { TONE } from "./Primitives";

const COUNT_TONE = {
  red: "text-red-400",
  amber: "text-amber-400",
  blue: "text-sky-400",
  green: "text-emerald-400",
  gold: "text-primary",
  neutral: "text-muted-foreground",
};

/**
 * Compact action list: label on the left, right-aligned coloured count,
 * each row navigating to the filtered list.
 */
export const NeedsAttention = ({ items = [], testid = "needs-attention", title = "Needs attention", label = "Action queue" }) => {
  const rows = items.filter((i) => i && (i.count > 0 || i.always));
  return (
    <div className="border border-border bg-card" data-testid={testid}>
      <div className="px-5 py-4 border-b border-border flex items-center justify-between">
        <div>
          <div className="label-mono">{label}</div>
          <h3 className="font-heading text-lg mt-0.5">{title}</h3>
        </div>
        <Warning size={17} className={rows.length ? "text-amber-400" : "text-muted-foreground"} />
      </div>
      {rows.length === 0 ? (
        <div className="px-5 py-10 text-center">
          <div className="font-heading text-base text-emerald-400">All clear</div>
          <div className="text-xs text-muted-foreground mt-1">Nothing needs your attention right now.</div>
        </div>
      ) : (
        <div className="divide-y divide-border/60">
          {rows.map((it) => {
            const body = (
              <div className="px-5 py-3.5 flex items-center gap-3">
                <span className="flex-1 text-sm min-w-0">{it.label}</span>
                <span className={`font-heading text-xl tnum ${COUNT_TONE[it.tone] || COUNT_TONE.neutral}`} data-testid={`${testid}-count-${it.key}`}>
                  {it.count}
                </span>
                {it.to && <CaretRight size={13} className="text-muted-foreground shrink-0" />}
              </div>
            );
            return it.to ? (
              <Link key={it.key} to={it.to} className="block hover:bg-white/[0.03] transition-colors duration-150" data-testid={`${testid}-row-${it.key}`}>
                {body}
              </Link>
            ) : (
              <div key={it.key} data-testid={`${testid}-row-${it.key}`}>{body}</div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/**
 * Bordered urgency card: entity name, meta line, huge urgency-coloured
 * timer/date, and one gold full-width action.
 */
export const QueueCard = ({ title, meta, figure, figureLabel, urgency = "neutral", action, badges, testid }) => {
  const ring = urgency === "red" ? "border-red-500/35" : urgency === "amber" ? "border-amber-500/35" : urgency === "green" ? "border-emerald-500/30" : "border-border";
  const fig = urgency === "red" ? "urg-red" : urgency === "amber" ? "urg-amber" : urgency === "green" ? "urg-green" : "text-foreground";
  return (
    <div className={`border ${ring} bg-card p-5 flex flex-col`} data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-heading text-lg truncate">{title}</div>
          {meta && <div className="text-xs text-muted-foreground mt-1 truncate">{meta}</div>}
        </div>
        {badges && <div className="flex flex-col items-end gap-1.5 shrink-0">{badges}</div>}
      </div>
      <div className="mt-5">
        {figureLabel && <div className="label-mono mb-1">{figureLabel}</div>}
        <div className={`font-heading text-3xl tnum leading-none ${fig}`} data-testid={testid ? `${testid}-figure` : undefined}>{figure}</div>
      </div>
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
};

/** Client / vehicle / lead card for grid views. */
export const EntityCard = ({ image, initials, name, sub, statLabel, statValue, keyFigure, badges, to, onClick, testid, icon: Icon }) => {
  const inner = (
    <div className="border border-border bg-card p-4 h-full flex flex-col hover:border-primary/30 transition-colors duration-200" data-testid={testid}>
      <div className="flex items-center gap-3">
        {image ? (
          <img src={image} alt={name} className="w-12 h-12 rounded-xl object-cover border border-border shrink-0" />
        ) : (
          <span className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center font-heading text-base text-primary shrink-0">
            {Icon ? <Icon size={18} /> : initials}
          </span>
        )}
        <div className="min-w-0 flex-1">
          <div className="font-heading text-base truncate">{name}</div>
          {sub && <div className="text-xs text-muted-foreground truncate mt-0.5">{sub}</div>}
        </div>
      </div>
      {badges && <div className="flex items-center gap-1.5 flex-wrap mt-3">{badges}</div>}
      {(statLabel || keyFigure) && (
        <div className="mt-4 pt-3 border-t border-border/60 flex items-end justify-between gap-3">
          <div className="min-w-0">
            {statLabel && <div className="label-mono">{statLabel}</div>}
            {statValue && <div className="text-sm mt-1 truncate">{statValue}</div>}
          </div>
          {keyFigure && <div className="font-heading text-lg tnum text-primary shrink-0">{keyFigure}</div>}
        </div>
      )}
    </div>
  );
  if (to) return <Link to={to} className="block h-full">{inner}</Link>;
  if (onClick) return <button type="button" onClick={onClick} className="block h-full w-full text-left">{inner}</button>;
  return inner;
};

/** Small tinted icon chip, matching StatTile's chip. */
export const IconChip = ({ icon: Icon, tone = "neutral", size = 15 }) => (
  <span className={`shrink-0 w-8 h-8 rounded-xl border flex items-center justify-center ${TONE[tone]}`}>
    <Icon size={size} />
  </span>
);
